import React, { useEffect, useState } from 'react'
import { supabase } from '../../supabaseClient'
import { useNavigate } from 'react-router-dom';

export default function BaristaOperations({ onGoToProfile }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true)
    const [stats, setStats] = useState({
        activeBaristas: 0,
        branches: 0,
        orderAccuracy: 96,
        moodShieldActive: 12
    })

    useEffect(() => {
        fetchBaristaStats()
    }, [])

    const fetchBaristaStats = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('staff')
                .select('*')
                .eq('role', 'Barista')
                .eq('status', 'Active')

            if (error) throw error

            const activeBaristas = data?.length || 0
            const branches = new Set(data?.map(e => e.assigned_branch) || []).size

            setStats(prev => ({
                ...prev,
                activeBaristas,
                branches
            }))
        } catch (err) {
            console.error("Error fetching barista stats:", err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Barista Operations & Performance</h1>
                    <p className="text-sm text-gray-600 mt-1">Monitor staff performance, order accuracy, and wellbeing metrics</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search orders, products..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6"/>
                            </svg>
                        </span>
                    </div>
                    <button className="p-2 hover:bg-gray-100 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <g fill="none" fillRule="evenodd">
                                <path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"/>
                                <path fill="currentColor" d="M5 9a7 7 0 0 1 14 0v3.764l1.822 3.644A1.1 1.1 0 0 1 19.838 18h-3.964a4.002 4.002 0 0 1-7.748 0H4.162a1.1 1.1 0 0 1-.984-1.592L5 12.764zm5.268 9a2 2 0 0 0 3.464 0zM12 4a5 5 0 0 0-5 5v3.764a2 2 0 0 1-.211.894L5.619 16h12.763l-1.17-2.342a2 2 0 0 1-.212-.894V9a5 5 0 0 0-5-5"/>
                            </g>
                        </svg>
                    </button>
                    <div className="text-right">
                        <p className="font-semibold text-gray-800">Coffee Shop Manila</p>
                        <p className="text-xs text-gray-600">
                            {new Date().toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                            })}
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-6 mb-8">
                    {/* Active Baristas - Dynamic */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="mb-2">
                            <p className="text-sm text-gray-600">Active Baristas</p>
                        </div>
                        <div className="flex items-end gap-2">
                            {loading ? (
                                <p className="text-3xl font-bold text-blue-600">...</p>
                            ) : (
                                <p className="text-3xl font-bold text-blue-600">{stats.activeBaristas}</p>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            Across All Branches
                        </p>
                    </div>

                    {/* Order Accuracy - Static */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="mb-2">
                            <p className="text-sm text-gray-600">Order Accuracy</p>
                        </div>
                        <div className="flex items-end gap-2">
                            <p className="text-3xl font-bold text-green-600">{stats.orderAccuracy}%</p>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">+2.3% vs last week</p>
                    </div>

                    {/* Mood Shield Active - Static */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="mb-2">
                            <p className="text-sm text-gray-600">Mood Shield Active</p>
                        </div>
                        <div className="flex items-end gap-2">
                            <p className="text-3xl font-bold text-purple-600">{stats.moodShieldActive}</p>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">Staff using feature</p>
                    </div>
                </div>

                {/* Performance Charts Section */}
                <div className="grid grid-cols-2 gap-6 mb-8">
                    {/* Order Accuracy Trend */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Accuracy Trend</h3>
                        <div className="h-64 flex items-center justify-center text-gray-400">
                            <p>Chart visualization would go here</p>
                        </div>
                    </div>

                    {/* Barista Performance */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Performing Baristas</h3>
                        <div className="h-64 flex items-center justify-center text-gray-400">
                            <p>Performance leaderboard would go here</p>
                        </div>
                    </div>
                </div>

                {/* Wellbeing Metrics */}
                <div className="bg-white rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Staff Wellbeing Overview</h3>
                    <div className="grid grid-cols-4 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Average Mood</p>
                            <p className="text-2xl font-bold text-blue-600">8.2/10</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Break Compliance</p>
                            <p className="text-2xl font-bold text-green-600">94%</p>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Avg Hours/Week</p>
                            <p className="text-2xl font-bold text-yellow-600">38.5</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Satisfaction Rate</p>
                            <p className="text-2xl font-bold text-purple-600">92%</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}